import { DateTime } from 'luxon';
import { SpinnerService } from 'src/app/base/spinner/spinner.service';
import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { UserManagementData, UserType } from './../models/user-management-data';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { CommandModel, GridModule } from '@syncfusion/ej2-angular-grids';
import { UserManagementService } from './user-management.service';
import { AvpCustomAdaptor } from '../shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { ClientService } from './../services/client/client.service';
import { Role, UpdateUserInfoDTO, CreateUserDTO, ClientDTO } from './../models/models.swagger.generated';
import { OnInit, Component, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { UserInfoDTO } from '../models/models.swagger.generated';
import { AuthService } from '../services/auth.service';
import { ActivatedRoute } from '@angular/router';
import { UserEditFormType } from '../user-edit-form/user-edit-form-type';
import { SfCommandButtonType } from '../models/syncfusion-data';
import { UserEditFormComponent } from '../user-edit-form/user-edit-form.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../shared/search-field/search-field.component';
import { ClientAutocompleteComponent } from '../shared/client-autocomplete/client-autocomplete.component';

@Component({
  selector: "app-user-management",
  templateUrl: "./user-management.component.html",
  styleUrls: ["./user-management.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ClientAutocompleteComponent,
    SearchFieldComponent,
    CheckBoxModule,
    GridModule,
    ToolbarModule,
    UserEditFormComponent,
    TranslatePipe,
  ],
})
export class UserManagementComponent
  extends GridBaseComponent<UserEditFormType>
  implements OnInit
{
  public languageFilter = GridConstantsFactory.createCheckBoxFilter();
  public roleFilter = GridConstantsFactory.createCheckBoxFilter();
  public applicationTypeFilter = GridConstantsFactory.createCheckBoxFilter();

  private addUserType = UserType.Web;

  constructor(
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
    translateService: TranslateService,
    private authService: AuthService,
    clientService: ClientService,
    private route: ActivatedRoute,
    private spinnerService: SpinnerService,
  ) {
    super(clientService, translateService, userManagementService);
    this.gridCommands = [
      {
        type: SfCommandButtonType.Edit,
        buttonOption: {
          cssClass: "icon-btn",
          iconCss: "icon-edit e-icons",
        },
      },
    ] as CommandModel[];
    this.searchOptions = {
      fields: ["firstname", "lastname", "client.name", "email"],
    };
  }

  public ngOnInit() {
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(
        this.userManagementService,
        this.spinnerService,
      ),
    });

    this.route.queryParams.subscribe((params) => {
      const clientId = +params.clientId;
      const newSelectedClient = this.clientService?.clients?.find(
        (c) => c.id === clientId,
      );
      if (newSelectedClient) {
        this.clientService!.selectedClient = newSelectedClient;
      }
    });

    this.isActiveColumnName = "enabled";
    this.editDialogTitle = "i18n.base.user-add-modal.titleEdit";
    this.addDialogTitle = "i18n.base.user-add-modal.titleAdd";
    this.addIndexFindColumnName = "userName";
  }

  public addUser(isMobileUser: boolean) {
    this.addUserType = isMobileUser ? UserType.Mobile : UserType.Web;
    this.onAdd();
  }

  public formatRoles(userInfo: UserInfoDTO): string {
    if (!userInfo || !userInfo.roles) {
      return "";
    }
    return userInfo.roles
      .map((r) =>
        this.translateService.instant("i18n.shared.enums.Role." + Role[r]),
      )
      .join(", ");
  }

  protected override getDataToSave(): any {
    const userManagementData = new UserManagementData();
    userManagementData.data = this.mapToUserInfoDTO(this.gridEditForm);
    userManagementData.type = this.gridEditForm?.value.userType ?? null;
    return userManagementData;
  }

  protected initializeEditForm(data: UserInfoDTO): void {
    const client =
      this.authService.getUserInfo()?.client ??
      (data.userId ? data.client : this.clientService?.selectedClient);

    let userType = UserType.Web;
    if (!data.userId) {
      // we are in add mode
      userType = this.addUserType;
    } else if (data.roles && data.roles.length > 0) {
      userType = data.roles[0] === Role.MobileUser ? UserType.Mobile : UserType.Web;
    }

    this.gridEditForm = this.fb.group<UserEditFormType>({
      userId: this.fb.control<string | null>(data.userId ?? null),
      firstname: this.fb.control<string | null>(data.firstname ?? null, [
        Validators.required,
      ]),
      lastname: this.fb.control<string | null>(data.lastname ?? null, [
        Validators.required,
      ]),
      language: this.fb.control<string | null>(data.language ?? null, [
        Validators.required,
      ]),
      email: this.fb.control<string | null>(data.email ?? null, [
        Validators.required,
        Validators.email,
      ]),
      phoneNumber: this.fb.control<string | null>(data.phoneNumber ?? null, [
        CustomValidatorsFactory.createPhoneValidator(),
      ]),
      enabled: this.fb.control<boolean>(data.enabled ?? false),
      client: this.fb.control<ClientDTO | null>(
        { value: client ?? null, disabled: !!data.userId },
        [Validators.required],
      ),
      accessCode: this.fb.control<string | null>(data.accessCode ?? null),
      accessCodeValidUntil: this.fb.control<Date | null>(
        data.accessCodeValidUntil?.toJSDate() ?? null,
        [CustomValidatorsFactory.datePickerValidator()],
      ),
      accessCodeGeneratedOn: this.fb.control<DateTime | null>(
        data.accessCodeGeneratedOn ?? null,
      ),
      accessCodeUsedOn: this.fb.control<DateTime | null>(
        data.accessCodeUsedOn ?? null,
      ),
      role: this.fb.control<Role | null>(
        !!data.roles?.length ? data.roles[0] : null,
        [Validators.required],
      ),
      userType: this.fb.control<UserType | null>(userType),
    });

    if (userType === UserType.Mobile) {
      this.gridEditForm.get("phoneNumber")?.setValidators([Validators.required]);
      if (!data.userId) {
        // new mobile user -> no roles required
        this.gridEditForm.get("role")?.setValidators([]);
      }
    }
  }

  protected getEditDialogTitleParams(rowData: any): object {
    return {};
  }

  private mapToUserInfoDTO(
    form: FormGroup<UserEditFormType> | null,
  ): UpdateUserInfoDTO | CreateUserDTO | null {
    if (!form) {
      return null;
    }
    const formData = form.value;
    let dto: UpdateUserInfoDTO | CreateUserDTO | null = null;
    if (!formData.userId) {
      dto = new CreateUserDTO();
    } else {
      dto = new UpdateUserInfoDTO();
      dto.userId = formData.userId;
      dto.enabled = formData.enabled ?? undefined;
      dto.accessCodeValidUntil = formData.accessCodeValidUntil
      ? DateTime.fromJSDate(formData.accessCodeValidUntil)
      : undefined;
    }
    dto.firstname = formData.firstname ?? undefined;
    dto.lastname = formData.lastname ?? undefined;
    dto.clientId = formData.client?.id ?? undefined;
    dto.email = formData.email ?? undefined;
    dto.language = formData.language ?? undefined;
    dto.phoneNumber = formData.phoneNumber ?? undefined;
    dto.roles = [formData.role!];

    return dto;
  }
}

